(function () {
  'use strict';

  // Defaults are the NZ (Auckland) warehouse. The snippet can switch a widget to the Australian
  // warehouse (G3PL, Victoria) with data-tz="Australia/Melbourne" data-region="AU-VIC".
  var TZ = 'Pacific/Auckland';
  var REGION = 'NZ-AKL';
  var LOCALE = 'en-NZ';

  // Matariki dates from NZ Government (published through 2030)
  var MATARIKI = {
    2024: [6, 28], 2025: [6, 20], 2026: [7, 10],
    2027: [6, 25], 2028: [7, 14], 2029: [7,  6], 2030: [6, 21],
  };

  /* ── Date utilities ──────────────────────────────────────── */

  // Parse a JS Date into NZT components
  function nzt(d) {
    var parts = {};
    new Intl.DateTimeFormat(LOCALE, {
      timeZone: TZ,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).formatToParts(d).forEach(function (p) { parts[p.type] = p.value; });

    var h = +parts.hour === 24 ? 0 : +parts.hour;
    var y = +parts.year, mo = +parts.month - 1, day = +parts.day;
    return {
      y: y, mo: mo, d: day,
      h: h, mi: +parts.minute, s: +parts.second,
      dow: new Date(y, mo, day).getDay(), // 0=Sun … 6=Sat
      key: parts.year + '-' + parts.month + '-' + parts.day,
    };
  }

  // Add n whole days to a Date (millisecond addition — timezone-safe)
  function addDays(d, n) { return new Date(d.valueOf() + n * 86400000); }

  // Create a stable "marker" date for a NZT calendar date.
  // Using UTC noon avoids DST boundary issues when iterating.
  function nztDate(y, mo, day) {
    return new Date(Date.UTC(y, mo, day, 0, 0, 0));
  }

  /* ── Easter (Anonymous Gregorian algorithm) ──────────────── */
  function easter(y) {
    var a=y%19, b=Math.floor(y/100), c=y%100, d=Math.floor(b/4),
        e=b%4, f=Math.floor((b+8)/25), g=Math.floor((b-f+1)/3),
        h=(19*a+b-d-g+15)%30, i=Math.floor(c/4), k=c%4,
        l=(32+2*e+2*i-h-k)%7, m=Math.floor((a+11*h+22*l)/451),
        mo=Math.floor((h+l-7*m+114)/31),
        day=((h+l-7*m+114)%31)+1;
    return nztDate(y, mo - 1, day);
  }

  /* ── NZ (Auckland) public holiday set for a given year ───── */
  var _cache = {};

  /* ── Victoria (G3PL warehouse) public holiday set ─────────── */
  // AFL Grand Final Friday is set each year by the Victorian government and is not included.
  function buildHolidaysVIC(y) {
    var s = new Set();
    function add(d) { s.add(nzt(d).key); }
    function nthMonday(month, n) {
      var d = nztDate(y, month, 1), c = 0;
      while (true) { if (nzt(d).dow === 1 && ++c === n) return d; d = addDays(d, 1); }
    }
    function weekendToMonday(d) {
      var dw = nzt(d).dow;
      return dw === 6 ? addDays(d, 2) : dw === 0 ? addDays(d, 1) : d;
    }
    add(weekendToMonday(nztDate(y, 0, 1)));          // New Year's Day
    add(weekendToMonday(nztDate(y, 0, 26)));         // Australia Day
    add(nthMonday(2, 2));                            // Labour Day: 2nd Monday of March
    var e = easter(y);
    add(addDays(e, -2)); add(addDays(e, -1)); add(e); add(addDays(e, 1)); // Good Friday to Easter Monday
    add(nztDate(y, 3, 25));                          // Anzac Day (no substitute day in Victoria)
    add(nthMonday(5, 2));                            // King's Birthday: 2nd Monday of June
    var cup = nztDate(y, 10, 1);                     // Melbourne Cup: 1st Tuesday of November
    while (nzt(cup).dow !== 2) cup = addDays(cup, 1);
    add(cup);
    var xmas = nztDate(y, 11, 25), box = nztDate(y, 11, 26), xdw = nzt(xmas).dow;
    if (xdw === 6 || xdw === 0) { add(addDays(xmas, xdw === 6 ? 2 : 1)); add(addDays(box, xdw === 6 ? 2 : 1)); }
    else { add(xmas); add(weekendToMonday(box)); }
    return s;
  }

  function buildHolidays(y) {
    if (REGION === 'AU-VIC') return (_cache[REGION + y] = buildHolidaysVIC(y));
    var s = new Set();
    function add(d) { s.add(nzt(d).key); }

    // Helper: shift a date to Monday if it lands on a weekend
    function mondayise(d) {
      var dw = nzt(d).dow;
      if (dw === 6) return addDays(d, 2); // Sat → Mon
      if (dw === 0) return addDays(d, 1); // Sun → Mon
      return d;
    }

    // ── New Year's Day & Day After New Year's ──
    var ny1 = nztDate(y, 0, 1), ny2 = nztDate(y, 0, 2);
    var ny1dw = nzt(ny1).dow, ny2dw = nzt(ny2).dow;
    if (ny1dw === 6) {          // Jan 1 Sat → Mon Jan 3, Tue Jan 4
      add(addDays(ny1, 2)); add(addDays(ny2, 2));
    } else if (ny1dw === 0) {   // Jan 1 Sun → Mon Jan 2, Tue Jan 3
      add(addDays(ny1, 1)); add(addDays(ny2, 1));
    } else {
      add(ny1);
      add((ny2dw === 6) ? addDays(ny2, 2) : (ny2dw === 0) ? addDays(ny2, 1) : ny2);
    }

    // ── Auckland Anniversary — last Monday of January ──
    var d = nztDate(y, 0, 31); // Jan 31 (always exists)
    while (nzt(d).dow !== 1) d = addDays(d, -1);
    add(d);

    // ── Waitangi Day — Feb 6 (Mondayised) ──
    add(mondayise(nztDate(y, 1, 6)));

    // ── Easter Friday & Easter Monday ──
    var e = easter(y);
    add(addDays(e, -2));
    add(addDays(e,  1));

    // ── Anzac Day — Apr 25 (Mondayised) ──
    add(mondayise(nztDate(y, 3, 25)));

    // ── King's Birthday — 1st Monday of June ──
    d = nztDate(y, 5, 1);
    while (nzt(d).dow !== 1) d = addDays(d, 1);
    add(d);

    // ── Matariki (lookup table) ──
    if (MATARIKI[y]) add(nztDate(y, MATARIKI[y][0] - 1, MATARIKI[y][1]));

    // ── Labour Day — 4th Monday of October ──
    d = nztDate(y, 9, 1);
    var count = 0;
    while (count < 4) {
      if (nzt(d).dow === 1) count++;
      if (count < 4) d = addDays(d, 1);
    }
    add(d);

    // ── Christmas Day & Boxing Day ──
    var xmas = nztDate(y, 11, 25), box = nztDate(y, 11, 26);
    var xdw = nzt(xmas).dow, bdw = nzt(box).dow;
    if (xdw === 6) {          // Xmas Sat → Mon Dec 27, Tue Dec 28
      add(addDays(xmas, 2)); add(addDays(box, 2));
    } else if (xdw === 0) {   // Xmas Sun → Mon Dec 26, Tue Dec 27
      add(addDays(xmas, 1)); add(addDays(box, 1));
    } else {
      add(xmas);
      add((bdw === 6) ? addDays(box, 2) : (bdw === 0) ? addDays(box, 1) : box);
    }

    return (_cache[REGION + y] = s);
  }

  function getHolidays(y) { return _cache[REGION + y] || buildHolidays(y); }

  /* ── Business day helpers ────────────────────────────────── */

  function isBizDay(d) {
    var p = nzt(d);
    if (p.dow === 0 || p.dow === 6) return false;
    return !getHolidays(p.y).has(p.key);
  }

  function nextBizDay(d) {
    var next = addDays(d, 1);
    while (!isBizDay(next)) next = addDays(next, 1);
    return next;
  }

  function addBizDays(d, n) {
    var cur = d;
    for (var i = 0; i < n; i++) cur = nextBizDay(cur);
    return cur;
  }

  /* ── Formatting ──────────────────────────────────────────── */

  function fmtDate(d) {
    return d.toLocaleDateString(LOCALE, {
      timeZone: TZ, weekday: 'short', day: 'numeric', month: 'short',
    });
  }

  /* ── Core render function ────────────────────────────────── */

  function parseCutoff(str) {
    if (!str || str.indexOf(':') === -1) return null;
    var parts = str.split(':');
    return { h: +parts[0], m: +parts[1] };
  }

  function render(widget, cfg) {
    var now = new Date();
    var p = nzt(now);

    // Determine today's cutoff time
    var cutoff = null;
    if (p.dow >= 1 && p.dow <= 5) cutoff = parseCutoff(cfg.weekday);
    else if (p.dow === 6) cutoff = parseCutoff(cfg.saturday);
    // Sunday (p.dow === 0) → no dispatch

    // Seconds remaining until cutoff
    var secsLeft = null;
    if (cutoff) {
      var cutoffSecs = cutoff.h * 3600 + cutoff.m * 60;
      var nowSecs = p.h * 3600 + p.mi * 60 + p.s;
      secsLeft = cutoffSecs - nowSecs;
    }

    var beforeCutoff = secsLeft !== null && secsLeft > 0;
    var todayDate = nztDate(p.y, p.mo, p.d);

    // Dispatch date: today if before cutoff, else next business day
    var dispatchDate = beforeCutoff ? todayDate : nextBizDay(todayDate);

    // Estimated delivery window
    var earliest = addBizDays(dispatchDate, cfg.min);
    var latest   = addBizDays(dispatchDate, cfg.max);
    var eStr = fmtDate(earliest), lStr = fmtDate(latest);
    var delivery = eStr === lStr ? eStr : eStr + ' \u2013 ' + lStr;

    // Build message
    var inner, urgent = false;

    if (beforeCutoff) {
      var h = Math.floor(secsLeft / 3600);
      var m = Math.floor((secsLeft % 3600) / 60);
      var s = secsLeft % 60;
      var timer = h > 0
        ? h + 'h ' + m + 'm ' + String(s).padStart(2, '0') + 's'
        : m + 'm ' + String(s).padStart(2, '0') + 's';
      urgent = secsLeft < 3600;
      inner = 'Order within <strong>' + timer + '</strong> for estimated delivery <strong>' + delivery + '</strong>';

    } else if (isBizDay(todayDate) && secsLeft !== null && secsLeft <= 0) {
      // After today's cutoff on a business day
      inner = 'Order today for estimated delivery <strong>' + delivery + '</strong>';

    } else {
      // Weekend, public holiday, or day with no dispatch configured
      inner = 'Next dispatch <strong>' + fmtDate(dispatchDate) + '</strong> \u00b7 estimated delivery <strong>' + delivery + '</strong>';
    }

    widget.innerHTML = '<p class="order-deadline__text' + (urgent ? ' order-deadline__text--urgent' : '') + '">' + inner + '</p>';
    widget.classList.toggle('order-deadline--urgent', urgent);
  }

  /* ── Init ────────────────────────────────────────────────── */

  document.addEventListener('DOMContentLoaded', function () {
    var widget = document.getElementById('order-deadline-widget');
    if (!widget) return;
    if (widget.dataset.tz) TZ = widget.dataset.tz;
    if (widget.dataset.region) REGION = widget.dataset.region;
    if (REGION === 'AU-VIC') LOCALE = 'en-AU';

    var cfg = {
      weekday:  widget.dataset.cutoffWeekday  || '12:30',
      saturday: widget.dataset.cutoffSaturday || '',
      min: +(widget.dataset.deliveryMin || 1),
      max: +(widget.dataset.deliveryMax || 3),
    };

    render(widget, cfg);
    setInterval(function () { render(widget, cfg); }, 1000);
  });

})();
